import { useState } from "react";
import PropTypes from "prop-types";
// import api from "api/api";

// mui components
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// redux
import { useSelector } from "react-redux";

// introduction assets
// business and research objective
import actionStandardsMonadic from "../../assets/autoDeckReport/introduction/action-standards-monadic.png";
import actionStandardsSeqMonadic from "../../assets/autoDeckReport/introduction/action-standards-seq-monadic.png";
import noActionStandards from "../../assets/autoDeckReport/introduction/no-action-standards.png";

// product details
import productDetails from "../../assets/autoDeckReport/introduction/product-details.png";

// the project flow
import projectFlowMonadic from "../../assets/autoDeckReport/introduction/project-flow-monadic.png";
import projectFlowSeqMonadic from "../../assets/autoDeckReport/introduction/project-flow-seq-monadic.png";

// sample specifications
import sampleSpecificationsCommon from "../../assets/autoDeckReport/introduction/sample-specification-common.png";
import sampleSpecificationsNoUserProfile from "../../assets/autoDeckReport/introduction/sample-specification-no-user-profile.png";
import sampleSpecificationsOneUserProfile from "../../assets/autoDeckReport/introduction/sample-specification-one-user-profile.png";
import sampleSpecificationsTwoUserProfile from "../../assets/autoDeckReport/introduction/sample-specification-two-user-profile.png";
import sampleSpecificationsthreeUserProfile from "../../assets/autoDeckReport/introduction/sample-specification-three-user-profile.png";

function PreviewDialog({ selectedCriterionOptions = {}, selectedCriteria = "" }) {
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewSlides, setPreviewSlides] = useState([]);
    const hasPreviewData = selectedCriterionOptions[selectedCriteria]?.length > 0;

    // redux
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const actionStandards = useSelector((state) => state.autoDeckReportStore.actionStandards);
    const selectedUserProfiles = useSelector((state) => state.autoDeckReportStore.selectedUserProfiles);

    const handlePreviewDialogClose = () => {
        setPreviewDialogOpen(!previewDialogOpen);
    };

    const handlePreview = () => {
        const slides = [];
        const options = selectedCriterionOptions[selectedCriteria] ?? [];
        const selectedStudyItem = selectedStudy[0];
        const testDesign = selectedStudyItem?.test_design ?? "";
        const coreDataId = selectedStudyItem?.id ?? "";
        const userProfilesLength = selectedUserProfiles?.length ?? 0;

        if (selectedCriteria === "introduction") {
            const hasActionStandards = actionStandards.some((datum) => datum.action_standard_groups?.core_data_model_id === coreDataId);

            const isSeqMonadic = testDesign.includes("Seq. Monadic");
            const isMonadic = testDesign.includes("Monadic");

            // Business Research Objective
            if (options.includes("business_research_objective")) {
                if (!hasActionStandards) {
                    slides.push(noActionStandards);
                } else if (isSeqMonadic) {
                    slides.push(actionStandardsSeqMonadic);
                } else if (isMonadic) {
                    slides.push(actionStandardsMonadic);
                }
            }

            // Product Details
            if (options.includes("product_details")) {
                slides.push(productDetails);
            }

            // Project Flow
            if (options.includes("the_project_flow")) {
                if (isSeqMonadic) {
                    slides.push(projectFlowSeqMonadic);
                } else if (isMonadic) {
                    slides.push(projectFlowMonadic);
                }
            }

            // Sample Specifications
            if (options.includes("sample_specifications")) {
                const userProfileSlides = {
                    0: sampleSpecificationsNoUserProfile,
                    1: sampleSpecificationsOneUserProfile,
                    2: sampleSpecificationsTwoUserProfile,
                    3: sampleSpecificationsthreeUserProfile,
                };

                const sampleSpecificationSlide = userProfileSlides[userProfilesLength];

                if (sampleSpecificationSlide) {
                    slides.push(sampleSpecificationSlide, sampleSpecificationsCommon);
                }
            }
        }

        setPreviewSlides(slides);
        setPreviewDialogOpen(true);
    };

    return (
        <>
            <Button
                size="large"
                className={!hasPreviewData ? "custom-btn me-4 disabled" : "custom-btn liner-light-blue-background me-4"}
                variant="contained"
                onClick={handlePreview}
                disabled={!hasPreviewData}
            >
                Preview Example
            </Button>
            {previewDialogOpen && (
                <Dialog
                    open={previewDialogOpen}
                    onClose={handlePreviewDialogClose}
                    fullWidth={true}
                    maxWidth="lg"
                >
                    <DialogTitle
                        style={{
                            padding: "20px",
                        }}
                        id="alert-dialog-title"
                    >
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 2,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handlePreviewDialogClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent style={{ padding: "0px 40px 20px 40px", overflow: "auto" }}>
                        <div>
                            <p style={{ color: "#005eef", fontSize: "1.75rem", fontWeight: "bold" }}>{selectedCriteria.toUpperCase()}</p>
                            {previewSlides.map((slide, index) => {
                                return (
                                    <img
                                        key={index}
                                        alt={slide}
                                        src={slide}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                                            marginBottom: "10px",
                                        }}
                                    />
                                );
                            })}
                        </div>
                        {(selectedCriterionOptions[selectedCriteria] ?? []).includes("sample_specifications") && (
                            <p style={{ fontSize: "12px", marginTop: "10px" }}>
                                <span style={{ fontWeight: "bold" }}>Note:</span> Each selected &apos;User Profile&apos; will have its own dedicated &apos;Sample Specification&apos; slide in the downloaded agency report.
                            </p>
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

PreviewDialog.propTypes = {
    selectedCriterionOptions: PropTypes.object,
    selectedCriteria: PropTypes.string,
};

export default PreviewDialog;
